import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService, ParamsObject } from '../../utils/api.service';
import { CourseInstance, Registration, ServiceBooking } from '../../models';
import { HttpParams } from '@angular/common/http';
import {
  CrudArrayActionServiceMandatory,
  Param,
  ParamProperty,
} from '../../../shared/crud-array-action/abstract-crud-array-action.directive';

@Injectable()
export class CourseInstanceService implements CrudArrayActionServiceMandatory {

  public reinitializePageLoading: Subject<void> = new Subject<void>();

  private path = 'course_instances';

  private triggerEditAction: Subject<CourseInstance> = new Subject<CourseInstance>();

  private triggerDeleteAction: Subject<CourseInstance> = new Subject<CourseInstance>();

  // eslint-disable-next-line @typescript-eslint/member-ordering
  public readonly paramAction: Param = {
    edit: {
      provide: true,
      triggerAction: this.triggerEditAction,
    },
    delete: {
      provide: true,
      triggerAction: this.triggerDeleteAction,
    },
  };

  constructor(
    private api: ApiService,
  ) {
  }

  public getTrigger(type: ParamProperty): Observable<CourseInstance> {
    return (type === 'edit' ? this.triggerEditAction : this.triggerDeleteAction).asObservable();
  }

  public setReinitializePageLoading(): void {
    this.reinitializePageLoading.next();
  }

  public getReinitializePageLoading(): Observable<void> {
    return this.reinitializePageLoading.asObservable();
  }

  public convertDataToSendToTrigger(data: any): number {
    return data;
  }

  public sendTriggerEdit(data: CourseInstance): void {
    this.paramAction.edit.triggerAction.next(data);
  }

  public sendTriggerDelete(data: CourseInstance): void {
    this.paramAction.delete.triggerAction.next(data);
  }

  delete(courseInstanceId: string) {
    return this.api.delete(`${this.path}/synchronized/${courseInstanceId}`, '', ['courseInstances']);
  }

  downloadAttendanceSheet(instanceId: string): Observable<any> {
    return this.api.download(`${this.path}/${instanceId}/attendance-sheet`);
  }

  downloadRemainingPlaces(catalogId: string, startDate?: Date, endDate?: Date): Observable<any> {
    let query = new HttpParams();
    if (startDate) {
      query = query.append('startDate', startDate.toISOString().split('T')[0]);
    }

    if (endDate) {
      query = query.append('endDate', endDate.toISOString().split('T')[0]);
    }

    return this.api.download(`${this.path}/extract/availabilities/${catalogId}`, query);
  }

  get(id: string): Observable<CourseInstance> {
    return this.api.get(this.path + '/' + id)
      .pipe(
        map(data => new CourseInstance(data)),
      );
  }

  getAll(params: ParamsObject = {}): Observable<CourseInstance[]> {
    const httpParameters = new HttpParams({
      fromObject: params,
    });
    return this.api.getSave(this.path, [httpParameters, 'courseInstances'])
      .pipe(map(data => {
        data['hydra:member'].map(obj => new CourseInstance(obj));
        return data;
      }));
  }

  /**
   * Returns the registrations of a course instance
   *
   * @param id Course instance identifier
   */
  getCourseInstanceRegistrations(id: string): Observable<Registration[]> {
    return this.api
      .get(`${this.path}/${id}/registrations`)
      .pipe(
        map(data => data.map(registration => new Registration(registration))),
      );
  }

  /**
   * Collection of the course instance service bookings
   *
   * @param id course instance id
   * @param params
   */
  getCourseInstanceServiceBookings(id: string, params: ParamsObject = null): Observable<ServiceBooking[]> {
    const httpParameters = new HttpParams({
      fromObject: params,
    });

    return this.api.get(`${this.path}/${id}/service_bookings`, httpParameters);
  }

  post(courseInstance): Observable<CourseInstance> {
    return this.api.post(this.path, courseInstance, '', ['courseInstances', 'roomBookings']);
  }

  put(courseInstance): Observable<CourseInstance> {
    return this.api.put(this.path + '/' + courseInstance.id, courseInstance, '', ['courseInstances', 'roomBookings']);
  }

  public isPropertyDisplay(property: ParamProperty, courseInstance: CourseInstance): boolean {
    if (new Date(courseInstance.endTime) < new Date()) {
      return false;
    }
    return courseInstance.dateDeleted == null;
  }

  closeCourseInstance(uuid: string): Observable<CourseInstance> {
    return this.api.get(this.path + `/${uuid}/close`)
    .pipe(
      map(data => data),
    );
  }
}
